import { reducedVfx } from "../../../ui/settings";
import { appliedTier } from "../../graphicsPrefs";
import { tierBand } from "../../qualityTiers";

/**
 * The kit's shared clock and gates: one set of uniform objects every kit material reads (no per-material updates).
 * `tick` runs from a kit mesh's onBeforeRender, so whichever renderer draws a skinned tower — the level, the hub, the
 * Skins turntable, a thumbnail — advances it, at most once per animation frame.
 *
 * Gates (the brief's "respect Reduced effects / low tiers"):
 *  - `motion` 0 freezes every procedural motion and pattern shimmer (Reduced effects, prefers-reduced-motion);
 *  - `detail` 0 hides detail-only pieces (twinkles, glints) at the low and lowest tiers and under Reduced effects.
 */
export class KitClock {
  static readonly time = { value: 0 };
  static readonly motion = { value: 1 };
  static readonly detail = { value: 1 };
  /** Tiers at or below this index (QUALITY_TIERS order: 0 ultra … 4 lowest) drop detail pieces. */
  static readonly DetailFromTier = 2;
  /** A fixed time for captures (thumbnails, the capture sheet): motion holds this pose. Null: live. */
  static frozenAt: number | null = null;
  private static lastFrame = -1;

  /** Advances the clock and re-reads the gates; cheap enough to call per kit draw (it runs once per frame). */
  static tick(now = performance.now()): void {
    const frame = Math.floor(now);
    if (frame === KitClock.lastFrame) return;
    KitClock.lastFrame = frame;
    KitClock.time.value = KitClock.frozenAt ?? now / 1000;
    const g = KitClock.gates();
    KitClock.motion.value = g.motion ? 1 : 0;
    KitClock.detail.value = g.detail ? 1 : 0;
  }

  /** The gates as booleans, from the settings and the applied quality tier (pure enough to test with stubs). */
  static gates(reduced = safeReduced(), tierIndex = safeTier()): { motion: boolean; detail: boolean; particles: boolean } {
    return { motion: !reduced, detail: !reduced && tierIndex <= KitClock.DetailFromTier, particles: !reduced && tierIndex <= KitClock.DetailFromTier + 1 };
  }
}

function safeReduced(): boolean { try { return reducedVfx(); } catch { return false; } }
function safeTier(): number { try { return tierBand(appliedTier()).index; } catch { return 1; } }
